import { H1, H2, P, UL, LI, Code, Note, Warn, StepList } from './primitives';

export function SectionSettings() {
  return (
    <>
      <H1>Settings &amp; Credentials</H1>
      <P>
        The <strong>Settings</strong> page (<Code>/settings</Code>) manages
        the global credentials used by all agent runs, notifications, and the
        observability endpoint.
      </P>

      <H2>Claude subscription token</H2>
      <P>
        The <Code>CLAUDE_CODE_OAUTH_TOKEN</Code> authorises the Claude CLI
        inside agent containers. Obtain it by running{' '}
        <Code>claude setup-token</Code> on a machine with Claude Code
        installed, then paste the output here.
      </P>
      <UL>
        <LI>Without this token, no agent run can start.</LI>
        <LI>
          The token is stored encrypted in Postgres and never returned to the
          browser.
        </LI>
      </UL>

      <H2>GitHub token</H2>
      <P>
        A <strong>classic</strong> personal access token used by all projects
        for clone / push / PR and the GitHub Issues backend, and by
        prototypes to create and delete their own repos. Fine-grained tokens
        can't reliably create or delete repositories, so use a classic token
        with these scopes:
      </P>
      <UL>
        <LI><Code>repo</Code> (clone, push, pull requests, issues)</LI>
        <LI>
          <Code>delete_repo</Code> (so a deleted prototype can remove its own
          private repo)
        </LI>
      </UL>

      <H2>Registry credentials (optional)</H2>
      <P>
        Only needed when your agent-runtime image lives in a private registry
        (e.g. a GitHub PAT with <Code>read:packages</Code> for ghcr.io). Leave
        empty for public images.
      </P>

      <H2>Telegram</H2>
      <P>
        Bot token + chat ID for run-finished notifications — see{' '}
        <em>Notifications</em>.
      </P>

      <H2>MCP</H2>
      <P>
        The observability MCP server at <Code>/api/mcp</Code> lets an AI tool
        (Claude Code, claude.ai, ChatGPT, Cursor) query your projects, runs,
        issues and prototypes. Add the URL to the client and approve the
        connection in the browser — there is no token to copy. A connection acts
        as you, in every workspace you belong to; <strong>Settings → MCP</strong>{' '}
        lists your connected apps and revokes them.
      </P>

      <H2>Integration tokens</H2>
      <P>
        Project-scoped bearer tokens (<Code>aiai_int_…</Code>) that let an
        external site's <strong>server</strong>, such as a &ldquo;try an app&rdquo; form, submit
        build requests to one project, poll them until they're live, and file
        follow-up feedback over <Code>/api/integrations/v1</Code>. Owners create
        and revoke them on the project's <strong>Team</strong> page. The token
        is shown once, reaches only that project, never acts as a user, and is
        rejected (401) as soon as it is revoked. Requests arrive as proposed
        issues tagged <Code>external</Code> and go through the Product Manager
        like any other ticket. The full recipe with curl examples is in{' '}
        <Code>docs/INTEGRATIONS.md</Code>.
      </P>
      <P>
        <strong>Workspace tokens</strong> (<strong>Workspace settings → Integration
        tokens</strong>) are the prototype factory: the site creates a brand-new prototype per
        visitor instead of filing into one project. See <em>Integration API</em> for the
        reference and a copy-ready prompt.
      </P>
      <P>
        Each token can also have a <strong>webhook</strong>: an https URL that
        receives the request status payload on every change, so the site
        doesn't have to poll. Set it with the token's <strong>Webhook</strong>
        button. The signing secret (<Code>whsec_…</Code>) is shown once.
        Deliveries carry <Code>X-Aiai-Signature: sha256=…</Code> (an HMAC-SHA256
        of the raw body) and <Code>X-Aiai-Timestamp</Code>. Failed deliveries are
        retried with exponential backoff for about 15 minutes. The panel shows
        the recent delivery log. URLs that resolve to private or loopback
        addresses are refused.
      </P>

      <H2>Security of stored credentials</H2>
      <P>
        All credentials are stored as AES-256-GCM ciphertext in Postgres. The
        encryption key is the <Code>SECRETS_MASTER_KEY</Code> environment
        variable (a 64-character hex string). The settings API only reports
        whether a credential is set — it never returns the value.
      </P>

      <Note>
        <strong>One token for all projects:</strong> grant the GitHub token
        access to every repository you link. For repos across multiple
        organisations, use a token whose scope spans them all.
      </Note>
    </>
  );
}

export function SectionNotifications() {
  return (
    <>
      <H1>Notifications</H1>
      <P>
        aiai·loop can message you on Telegram whenever a run finishes, so you
        don't have to keep the Activity page open to know when something needs
        attention.
      </P>

      <H2>Setting it up</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Create a Telegram bot',
            body: (
              <p>
                Message <Code>@BotFather</Code> on Telegram to create a bot
                and copy the bot token it gives you.
              </p>
            ),
          },
          {
            n: 2,
            title: 'Find the chat ID',
            body: (
              <p>
                Message your new bot, then open{' '}
                <Code>https://api.telegram.org/bot&lt;token&gt;/getUpdates</Code>{' '}
                in a browser to read back the chat (or channel) ID.
              </p>
            ),
          },
          {
            n: 3,
            title: 'Save it in Settings',
            body: (
              <p>
                Go to <strong>Settings → Notifications</strong> and paste the
                Telegram bot token and chat ID.
              </p>
            ),
          },
        ]}
      />

      <H2>When you're notified</H2>
      <P>A message is sent once a run reaches a terminal state:</P>
      <UL>
        <LI>
          <Code>succeeded</Code> — includes a link to the pull request, and
          whether it auto-merged or awaits your review.
        </LI>
        <LI><Code>failed</Code> — includes the error message.</LI>
        <LI>
          <Code>timed_out</Code> — the run exceeded the configured agent
          timeout.
        </LI>
      </UL>
      <P>
        When the operator has set a Claude API key, a merged pull request also
        gets a plain one-liner (“Shipped in Widgets: …”) with the PR link.
        Once a day at about 08:00 you also get a short summary per project of
        what shipped, what failed and what is queued. No message is sent on a
        quiet day.
      </P>

      <Note>
        There's no per-event toggle — every run that finishes notifies once.
        Telegram is the only channel that reaches you without an open tab.
      </Note>

      <H2>Browser notifications for PM replies in threads</H2>
      <P>
        On a thread page, tick{' '}
        <strong>Notify me when the PM replies</strong> to get a desktop
        notification whenever the Product Manager posts in one of the
        project's threads — unless you are already looking at that thread. The setting is per browser and
        off by default; your browser asks for permission the first time.
        Notifications only arrive while an aiai·loop tab is open, and clicking
        one opens the thread.
      </P>
    </>
  );
}

export function SectionSecurity() {
  return (
    <>
      <H1>Security (2FA)</H1>
      <P>
        The <strong>Security</strong> section of the <strong>Settings</strong>{' '}
        page manages two-factor authentication (TOTP) for your account.
      </P>

      <H2>Setting up TOTP</H2>
      <StepList
        steps={[
          {
            n: 1,
            title: 'Open Settings',
            body: (
              <p>
                Click <strong>Settings</strong> in the account menu and open
                the <strong>Security</strong> section.
              </p>
            ),
          },
          {
            n: 2,
            title: 'Click "Set up two-factor authentication"',
            body: <p>A QR code is shown alongside a manual entry key.</p>,
          },
          {
            n: 3,
            title: 'Scan the QR code',
            body: (
              <p>
                Use any TOTP app (Google Authenticator, Authy, 1Password,
                etc.) to scan the QR code or enter the manual key.
              </p>
            ),
          },
          {
            n: 4,
            title: 'Enter the verification code',
            body: (
              <p>
                Type the 6-digit code from your authenticator app and click{' '}
                <strong>Enable</strong>. This confirms the setup is working.
              </p>
            ),
          },
          {
            n: 5,
            title: 'Save your recovery codes',
            body: (
              <p>
                After enabling, recovery codes are shown once. Copy and store
                them securely. Each code can be used once to log in if you
                lose access to your authenticator.
              </p>
            ),
          },
        ]}
      />

      <H2>Logging in with 2FA</H2>
      <P>
        After entering your email and password, you are prompted for a TOTP
        code. Enter the 6-digit code from your authenticator app — or a
        recovery code if you've lost the device.
      </P>

      <H2>Recovery codes</H2>
      <P>
        Recovery codes are single-use backup codes. After using one, re-enrol
        a new authenticator as soon as possible.
      </P>

      <H2>Disabling 2FA</H2>
      <P>
        Go to <strong>Security</strong> and click <strong>Disable two-factor
        authentication</strong>. You will be asked to confirm.
      </P>

      <Warn>
        <strong>Deployment-enforced 2FA:</strong> two-factor is required by
        default (<Code>AUTH_REQUIRE_2FA=true</Code>) — until you set it up you
        cannot use the app, and the login flow redirects you to the enrolment
        page immediately. A deployment operator can relax this by setting{' '}
        <Code>AUTH_REQUIRE_2FA=false</Code>.
      </Warn>
    </>
  );
}
